import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  IconButton,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Grid,
  useTheme,
  alpha,
  CircularProgress
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { useSupabase } from '../contexts/SupabaseContext';
import { PieChart } from '@mui/x-charts';
import { chartPalette } from '../theme';

// Product type configurations
const PRODUCT_CONFIGS = {
  'savings-bank': {
    title: 'Savings Bank Accounts',
    table: 'wallets',
    type: 'bank',
    columns: 'id, name, balance as value',
    emptyMessage: 'No bank accounts found. Please add accounts in the Wallets section.'
  },
  'liquid-etf': {
    title: 'Liquid ETF Investments',
    table: 'investments',
    type: 'liquid_etf',
    columns: 'id, name, value, description',
    emptyMessage: 'No Liquid ETF investments found. Start investing to see them here.'
  },
  'mutual-fund': {
    title: 'Mutual Fund Investments',
    table: 'investments',
    type: 'mutual_fund',
    columns: 'id, name, value, description',
    emptyMessage: 'No mutual fund investments found. Start investing to see them here.'
  }
};

const ProductDetails = () => {
  const { assetClass, product } = useParams();
  const navigate = useNavigate();
  const theme = useTheme();
  const { supabase, user } = useSupabase();
  const [loading, setLoading] = useState(true);
  const [investments, setInvestments] = useState([]);
  const [totalValue, setTotalValue] = useState(0);

  const productConfig = PRODUCT_CONFIGS[product];

  const fetchInvestments = async () => {
    if (!user || !productConfig) return;
    
    try {
      setLoading(true);
      
      const { data, error } = await supabase
        .from(productConfig.table)
        .select(productConfig.columns)
        .eq('user_id', user.id)
        .eq('type', productConfig.type);

      if (error) {
        console.error('Error fetching data:', error);
        return;
      }

      const transformedData = data.map(item => ({
        id: item.id,
        name: item.name || item.description || 'Unnamed Investment',
        value: Number(item.value) || 0
      }));

      setInvestments(transformedData);
      const total = transformedData.reduce((sum, item) => sum + item.value, 0);
      setTotalValue(total);

    } catch (error) {
      console.error('Error:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!product || !PRODUCT_CONFIGS[product]) {
      navigate('/investments/cash-and-liquid');
      return;
    }
    fetchInvestments();
  }, [product, user]);

  const pieChartData = investments.map(item => ({
    id: item.id,
    value: item.value,
    label: item.name
  }));

  return (
    <Box sx={{ p: { xs: 2, sm: 3 } }}>
      {/* Header */}
      <Box sx={{ mb: 3, display: 'flex', alignItems: 'center', gap: 2 }}>
        <IconButton 
          onClick={() => navigate(`/investments/${assetClass}`)}
          sx={{ 
            color: 'text.secondary',
            '&:hover': { 
              color: 'text.primary',
              bgcolor: 'action.hover' 
            }
          }}
        >
          <ArrowBackIcon />
        </IconButton>
        <Box>
          <Typography variant="h5" sx={{ fontWeight: 600 }}>
            {productConfig?.title || 'Investment Details'}
          </Typography>
          <Typography variant="subtitle1" color="text.secondary">
            Total: {new Intl.NumberFormat('en-IN', {
              style: 'currency',
              currency: 'INR',
              maximumFractionDigits: 0
            }).format(totalValue)}
          </Typography>
        </Box>
      </Box>

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
          <CircularProgress />
        </Box>
      ) : investments.length === 0 ? (
        <Box sx={{ 
          display: 'flex', 
          flexDirection: 'column',
          alignItems: 'center',
          gap: 2,
          p: 4,
          color: 'text.secondary',
          textAlign: 'center'
        }}>
          <Typography variant="h6">No Investments Found</Typography>
          <Typography variant="body2">
            {productConfig?.emptyMessage || 'No data available.'}
          </Typography>
        </Box>
      ) : (
        <Grid container spacing={3}>
          {/* Left side - Table */}
          <Grid item xs={12} md={8}>
            <Paper
              sx={{ 
                borderRadius: 3,
                boxShadow: 'var(--fx-card-shadow)'
              }}
            >
              <Box sx={{ p: 2, borderBottom: 1, borderColor: 'divider' }}>
                <Typography variant="h6" sx={{ fontWeight: 600 }}>
                  Account Details
                </Typography>
              </Box>
              <TableContainer>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ fontWeight: 600 }}>Bank Name</TableCell>
                      <TableCell align="right" sx={{ fontWeight: 600 }}>Amount</TableCell>
                      <TableCell align="right" sx={{ fontWeight: 600 }}>In %</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {investments.map((investment) => (
                      <TableRow key={investment.id}>
                        <TableCell>{investment.name}</TableCell>
                        <TableCell align="right">
                          {new Intl.NumberFormat('en-IN', {
                            style: 'currency',
                            currency: 'INR',
                            maximumFractionDigits: 0
                          }).format(investment.value)}
                        </TableCell>
                        <TableCell align="right">
                          {((investment.value / totalValue) * 100).toFixed(1)}%
                        </TableCell>
                      </TableRow>
                    ))}
                    <TableRow sx={{ 
                      bgcolor: theme => alpha(theme.palette.primary.main, 0.05)
                    }}>
                      <TableCell sx={{ fontWeight: 600 }}>Total</TableCell>
                      <TableCell align="right" sx={{ fontWeight: 600 }}>
                        {new Intl.NumberFormat('en-IN', {
                          style: 'currency',
                          currency: 'INR',
                          maximumFractionDigits: 0
                        }).format(totalValue)}
                      </TableCell>
                      <TableCell align="right" sx={{ fontWeight: 600 }}>
                        100%
                      </TableCell>
                    </TableRow>
                  </TableBody>
                </Table>
              </TableContainer>
            </Paper>
          </Grid>

          {/* Right side - Pie Chart */}
          <Grid item xs={12} md={4}>
            <Paper
              sx={{ 
                height: '100%',
                borderRadius: 3,
                boxShadow: 'var(--fx-card-shadow)'
              }}
            >
              <Box sx={{ p: 2, borderBottom: 1, borderColor: 'divider' }}>
                <Typography variant="h6" sx={{ fontWeight: 600 }}>
                  Distribution
                </Typography>
              </Box>
              <Box sx={{ p: 2 }}>
                {pieChartData.length > 0 && (
                  <PieChart colors={chartPalette}
                    series={[
                      {
                        data: pieChartData,
                        highlightScope: { faded: 'global', highlighted: 'item' },
                        faded: { innerRadius: 30, additionalRadius: -30 },
                      },
                    ]}
                    height={300}
                  />
                )}
              </Box>
            </Paper>
          </Grid>
        </Grid>
      )}
    </Box>
  );
};

export default ProductDetails;